import { useCallback, useMemo } from "react";
import { Button } from "@/lib/components/Button/Button.tsx";
import { motion } from "motion/react";
import {
  IconLibrary,
  IconPhoto,
  IconPhotoEdit,
  IconSwitchHorizontal,
} from "@tabler/icons-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/lib/components/Select";
import GradientPromptTextarea from "./GradientPromptTextarea.tsx";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import Toast from "@/lib/components/Toast";
import {
  SourceImageKey,
  UpdateCurrentView,
  useViewTransitionProps,
  ViewStateOf,
} from "./creative-project-hooks.ts";
import { MainViewState, SetView } from "./creative-project-hooks.ts";
import { queryClient } from "@/lib/queries/query-client.ts";
import { animatedImagePresetQueryKeys } from "@/lib/queries/animated-image-preset-queries.ts";
import ImagePreview from "./ImagePreview";
import Image from "@/lib/components/Image.tsx";
import { cn } from "@/lib/utils.ts";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { ActionIdentifier } from "@/lib/stores/editor-store/creative-project-state.ts";
import { emitInspirationAction } from "./Inspiration/inspiration-action-events.ts";
import {
  calculateCreativeAnimationCredits,
  CREDITS_CREATIVE_ANIMATION_PER_SECOND,
} from "@repo/api/utils/billing";
import { useTeamQuery } from "@/lib/queries/team-queries";
import CreditsText from "@/features/Billing/CreditsText";
import { TeamDto } from "@repo/api/team";
import { AnalyticsApi } from "@/lib/api/analytics-api";

function AnimationView({
  mainView,
  setMainView,
  updateCurrentMainView,
}: {
  mainView: ViewStateOf<MainViewState, "ANIMATION">;
  setMainView: SetView<MainViewState>;
  updateCurrentMainView: UpdateCurrentView<MainViewState>;
}) {
  const transitionProps = useViewTransitionProps();

  const imagePreviewPrimaryAction = useMemo(() => {
    return {
      label: "Edit image",
      icon: <IconPhotoEdit width={24} height={24} strokeWidth={2.5} />,
      onClick: () => {
        emitInspirationAction(
          mainView.sourceImageKey === "startSourceImageExternalId"
            ? ActionIdentifier.NAVIGATE_START_IMAGE
            : ActionIdentifier.NAVIGATE_END_IMAGE,
        );

        setMainView(
          { mode: "EDIT_IMAGE", sourceImageKey: mainView.sourceImageKey },
          "SLIDE_FROM_RIGHT",
        );
      },
    };
  }, [setMainView, mainView.sourceImageKey]);

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center overflow-hidden"
      {...transitionProps}
    >
      <div className="flex w-full max-w-212 flex-1 flex-col overflow-hidden p-8">
        <div className="h-7" />
        <ImagePreview
          sourceImageKey={mainView.sourceImageKey}
          primaryAction={imagePreviewPrimaryAction}
        />
        <div className="h-19.5" />
        <AnimationPromptInput
          selectedSourceImageKey={mainView.sourceImageKey}
          setMainView={setMainView}
          updateCurrentMainView={updateCurrentMainView}
        />
      </div>
    </motion.div>
  );
}

function AnimationPromptInput({
  selectedSourceImageKey,
  setMainView,
  updateCurrentMainView,
}: {
  selectedSourceImageKey: SourceImageKey;
  setMainView: SetView<MainViewState>;
  updateCurrentMainView: UpdateCurrentView<MainViewState>;
}) {
  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const animationForm = useEditorStore(
    (state) => state.service.getCurrentCreativeProjectState()?.animationForm,
  );
  const { prompt = "", duration = 5 } = animationForm ?? {};
  const {
    updateCurrentCreativeProjectState,
    createCustomInputAnimatedImage,
    closeCreativeProjectSheet,
  } = useEditorStore((state) => state.service);

  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const { data: team } = useTeamQuery(teamExternalId);

  const credits = calculateCreativeAnimationCredits(
    duration,
    team?.billingInfo.customPricing ?? null,
  );

  const handleSubmit = async () => {
    if (!prompt.trim() || !timelineItem?.externalId) return;
    try {
      await createCustomInputAnimatedImage({
        customInput: { prompt: prompt.trim(), duration },
      });
      AnalyticsApi.logCustomAnimate();
      emitInspirationAction(ActionIdentifier.ANIMATE_VIDEO);
      queryClient.invalidateQueries({
        queryKey: animatedImagePresetQueryKeys.all(),
      });
      closeCreativeProjectSheet();
      Toast.info("Custom animation started");
    } catch (e: any) {
      if (e?.response?.data?.errorCode === "NOT_ENOUGH_CREDITS") {
        return;
      }
      console.error(e);
      Toast.error("Failed to create custom animation");
    }
  };

  return (
    <div className="mt-auto flex flex-col pt-6">
      <div className="mb-3 flex items-end">
        <p className="mx-1.5 flex-1 pl-0 text-xl font-semibold text-text-primary">
          Custom animation
        </p>
        <ImageSwitcher
          selectedSourceImageKey={selectedSourceImageKey}
          updateCurrentMainView={updateCurrentMainView}
        />
      </div>

      <GradientPromptTextarea
        value={prompt}
        onChange={(value) =>
          updateCurrentCreativeProjectState((draft) => {
            if (draft.animationForm) {
              draft.animationForm.prompt = value;
            }
          })
        }
        onSubmit={handleSubmit}
        submitLabel={
          <div className="flex items-center">
            <span>Animate</span>
            <CreditsText
              credits={credits}
              isVisible={
                (team?.billingInfo.showCredits ?? false) &&
                (team?.billingInfo?.creditBalance ?? null) !== null
              }
            />
          </div>
        }
        source="ANIMATION"
        placeholder="Write a prompt for the animation..."
        actionsChildren={
          <>
            <Button
              size="sm"
              variant="quaternary"
              icon={<IconLibrary width={24} height={24} strokeWidth={2.5} />}
              iconPosition="leading"
              className="pl-3"
              onClick={() =>
                setMainView({ mode: "PROMPT_LIBRARY", source: "ANIMATION" })
              }
            >
              Recent prompts
            </Button>
            <DurationSelect
              duration={duration}
              onDurationChange={(value) =>
                updateCurrentCreativeProjectState((draft) => {
                  draft.animationForm.duration = value;
                })
              }
              team={team}
            />
          </>
        }
      />
    </div>
  );
}

const DURATION_OPTIONS = Array.from({ length: 13 }, (_, i) => i + 3);

function DurationSelect({
  duration,
  onDurationChange,
  team,
}: {
  duration: number;
  onDurationChange: (value: number) => void;
  team: TeamDto | undefined;
}) {
  return (
    <Select
      value={String(duration)}
      onValueChange={(value) => onDurationChange(Number(value))}
    >
      <SelectTrigger variant="quaternary" className="h-9 w-16">
        {duration}s
      </SelectTrigger>
      <SelectContent side="top" align="center">
        <div className="flex w-44 items-center justify-between px-2 pt-1.5 pb-1.5">
          <p className="text-sm font-semibold">Duration</p>
          {team?.billingInfo.showCredits ? (
            <div className="flex items-center">
              <img
                src="/CreditIcon.svg"
                alt=""
                className="mr-1 mb-0.5 ml-3 h-3.5 w-3.5"
              />
              <p className="text-sm font-semibold">
                {team.billingInfo.customPricing
                  ?.creditsCustomAnimationPerSecond ??
                  CREDITS_CREATIVE_ANIMATION_PER_SECOND}
                /sec
              </p>
            </div>
          ) : null}
        </div>
        {DURATION_OPTIONS.map((s) => (
          <SelectItem key={s} value={String(s)}>
            {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ImageSwitcher({
  selectedSourceImageKey,
  updateCurrentMainView,
}: {
  selectedSourceImageKey: SourceImageKey;
  updateCurrentMainView: UpdateCurrentView<MainViewState>;
}) {
  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const swapImagesHandler = useCallback(() => {
    if (!timelineItem) return;

    updateVideo((video) => {
      const item = video.compositionJson.mainSceneTimeline.find(
        (item) => item.externalId === timelineItem.externalId,
      );
      if (!item) return;

      const tempStartSourceImageExternalId = item.startSourceImageExternalId;
      const tempEndSourceImageExternalId = item.endSourceImageExternalId;

      // There should always be a start source image set.
      if (!tempEndSourceImageExternalId) return;

      item.startSourceImageExternalId = tempEndSourceImageExternalId;
      item.endSourceImageExternalId = tempStartSourceImageExternalId;
    });
  }, [timelineItem, updateVideo]);

  const endSourceImageExists = !!timelineItem?.endSourceImageExternalId;

  return (
    <div className="flex shrink-0 grow-0 basis-auto items-center gap-1">
      <StartEndImageButton
        sourceImageKey="startSourceImageExternalId"
        selectedSourceImageKey={selectedSourceImageKey}
        updateCurrentMainView={updateCurrentMainView}
      />

      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span className="inline-block w-fit">
            <Button
              variant="ghost"
              size="iconSm"
              onClick={swapImagesHandler}
              disabled={!endSourceImageExists}
            >
              <IconSwitchHorizontal size={16} />
            </Button>
          </span>
        </TooltipTrigger>
        {!endSourceImageExists && (
          <TooltipContent side="bottom">
            Add an end image to enable swapping.
          </TooltipContent>
        )}
      </Tooltip>

      <StartEndImageButton
        sourceImageKey="endSourceImageExternalId"
        selectedSourceImageKey={selectedSourceImageKey}
        updateCurrentMainView={updateCurrentMainView}
      />
    </div>
  );
}

function StartEndImageButton({
  sourceImageKey,
  selectedSourceImageKey,
  updateCurrentMainView,
}: {
  sourceImageKey: SourceImageKey;
  selectedSourceImageKey: SourceImageKey;
  updateCurrentMainView: UpdateCurrentView<MainViewState>;
}) {
  const timelineItem = useEditorStore((state) =>
    state.service.getCreativeProjectSheetTimelineItem(),
  );
  const sourceImage = useEditorStore((state) =>
    state.service.getSourceImageByExternalId(timelineItem?.[sourceImageKey]),
  );

  const handleSourceImageKeyChange = useCallback(
    (sourceImageKey: SourceImageKey) => {
      updateCurrentMainView((view) => {
        if (view.mode === "ANIMATION") {
          return { ...view, sourceImageKey };
        }

        throw new Error("Invalid view mode");
      });
    },
    [updateCurrentMainView],
  );

  const isSelected = sourceImageKey === selectedSourceImageKey;

  return (
    <Button
      asChild
      onClick={() => handleSourceImageKeyChange(sourceImageKey)}
      variant="plain"
      size="sm"
      className={cn(
        "shrink-0 gap-3 overflow-hidden rounded-md pr-4 pl-0 transition-none",
        {
          "border border-transparent": isSelected,
          "border border-grey-200 bg-white text-black-400": !isSelected,
        },
      )}
      style={{
        background: isSelected
          ? "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary-400) 30%, transparent), color-mix(in srgb, var(--color-secondary-400) 30%, transparent)) border-box"
          : undefined,
      }}
    >
      <div className="flex items-center gap-2">
        <div className="h-full w-14">
          {sourceImage ? (
            <Image
              src={sourceImage?.src}
              className="h-full w-full object-cover"
            />
          ) : (
            <div
              className={cn("flex h-full w-14 items-center justify-center", {
                "bg-grey-100 text-text-secondary": !isSelected,
                "relative bg-linear-to-r from-primary-400/8 to-secondary-400/8 [&_svg]:size-4 [&_svg]:stroke-[url(#icon-gradient)]":
                  isSelected,
              })}
            >
              <IconPhoto />
            </div>
          )}
        </div>

        <span
          className={cn({
            "relative bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent":
              isSelected,
            "text-text-secondary": !isSelected,
          })}
        >
          {sourceImageKey === "startSourceImageExternalId" ? "Start" : "End"}
        </span>
      </div>
    </Button>
  );
}

export default AnimationView;
